import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Archive, Loader2, Download, RotateCcw } from 'lucide-react';
import { formatDate, formatCurrency } from '../../lib/utils';

/**
 * Read-only list of offboarded tenants ("History" tab) for the Landlord/Manager
 * Tenants page. Extracted from the original LandlordTenants.jsx for maintainability.
 *
 * Props:
 *   history             — array of tenant_history records
 *   loaded              — boolean, whether the initial fetch has completed
 *   downloadingId       — history_id currently being downloaded (or null)
 *   reonboardingId      — history_id currently being re-onboarded (or null)
 *   onDownload(h)       — handler for "Statement PDF"
 *   onReonboard(h)      — handler for "Re-onboard"
 *   canReonboard        — boolean, whether the Re-onboard button should render
 *                         (e.g. landlords get true; property managers get
 *                          can('invite_tenants'))
 */
export default function TenantHistoryList({
  history = [],
  loaded = false,
  downloadingId = null,
  reonboardingId = null,
  onDownload,
  onReonboard,
  canReonboard = true,
}) {
  return (
    <Card data-testid="tenant-history-list">
      <CardContent className="p-0">
        {!loaded ? (
          <div className="p-12 text-center text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
          </div>
        ) : history.length === 0 ? (
          <div className="p-12 text-center">
            <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold text-lg">No offboarded tenants yet</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              When you offboard an active tenant, a permanent record will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {history.map((h) => (
              <div
                key={h.history_id}
                className="p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`history-row-${h.history_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-foreground truncate">{h.tenant_name || '—'}</h4>
                    <Badge variant="outline" className="text-xs">
                      {h.reason?.replace('_', ' ') || 'closed'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{h.tenant_email}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {h.property_name && (
                      <>
                        <span className="font-medium text-foreground">{h.property_name}</span>
                        {h.unit_label && ` · Unit ${h.unit_label}`}
                      </>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {h.move_in_date ? formatDate(h.move_in_date) : '—'} → {h.move_out_date ? formatDate(h.move_out_date) : '—'}
                    {h.total_paid > 0 && (
                      <>
                        {' · Total paid: '}
                        <span className="font-medium text-foreground">{formatCurrency(h.total_paid)}</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="text-right text-xs space-y-0.5 shrink-0">
                  {h.deposit_handling && h.deposit_handling !== 'none' && (
                    <p className="text-muted-foreground capitalize">
                      Deposit: <span className="font-medium text-foreground">{h.deposit_handling.replace('_', ' ')}</span>
                    </p>
                  )}
                  {h.deposit_amount_returned > 0 && (
                    <p className="text-emerald-700">+{formatCurrency(h.deposit_amount_returned)} returned</p>
                  )}
                  {h.deposit_amount_forfeited > 0 && (
                    <p className="text-red-600">-{formatCurrency(h.deposit_amount_forfeited)} forfeited</p>
                  )}
                  <p className="text-muted-foreground mt-1">
                    Offboarded {h.offboarded_at ? formatDate(h.offboarded_at) : ''}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDownload?.(h)}
                    disabled={downloadingId === h.history_id}
                    data-testid={`download-statement-${h.history_id}`}
                  >
                    {downloadingId === h.history_id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Statement PDF
                  </Button>
                  {canReonboard && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReonboard?.(h)}
                      disabled={reonboardingId === h.history_id}
                      className="text-[#6B8E6B] border-[#6B8E6B]/40 hover:bg-[#6B8E6B]/10"
                      data-testid={`reonboard-${h.history_id}`}
                    >
                      {reonboardingId === h.history_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Re-onboard
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
